import React, { ReactNode } from 'react';

import styled from 'styled-components';

import { EvidenceSection } from 'stile-shared/src/components/2024/layout/CardSection';

const StyledCardGridSection = styled<typeof EvidenceSection>(EvidenceSection)`
  .content {
    padding-top: 80px !important;
    padding-bottom: 150px !important;
    @media (max-width: 768px) {
      padding-top: 0px !important;
      padding-bottom: 60px !important;
    }
  }
  .--jumbo {
    margin: 0;
  }
  h2 {
    grid-column: 1 / span 12 !important;
    margin: 0 0 40px 0 !important;
    @media (max-width: 768px) {
      margin: 0 0 0 0 !important;
    }
  }
  .title {
    grid-column: 1 / span 12;
    max-width: none !important;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-direction: row;
    gap: 20px;
    margin-bottom: 40px;
    * {
      max-width: none !important;
    }
    h1,
    h2 {
      flex: 1 1 auto;
      max-width: none !important;
      margin: 0 !important;
      text-align: left !important;
    }
    a {
      flex: 0 0 auto;
      white-space: nowrap;
      max-height: 42px;
    }
  }
  @media (max-width: 768px) {
    padding-bottom: 0px;
    .title {
      flex-direction: column;
      align-items: flex-start;
      margin-bottom: 0;
      h1,
      h2 {
        width: 100%;
      }
      a {
        display: none;
      }
    }
    .mobile-button {
      display: flex;
      justify-content: center;
      grid-column: 1 / span 12 !important;
      padding-bottom: 40px;
    }
  }
  .card-grid {
    grid-column: 1 / span 12;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: 1fr;
    gap: 12px 13px;
    margin-top: 16px;
    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px 13px;
    }
    @media (max-width: 768px) {
      grid-template-columns: repeat(1, 1fr);
      grid-auto-rows: auto;
      margin-top: 32px;
    }
  }
`;

const StyledSection = styled(StyledCardGridSection)<{ $columns: number }>`
  .card-grid {
    grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr) !important;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr) !important;
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(1, 1fr) !important;
    }
  }

  .mobile-button-wrapper {
    display: none;

    @media (max-width: 768px) {
      display: flex;
      justify-content: center;
      grid-column: 1 / span 12 !important;
      margin-top: 32px;
    }
  }
`;

type CardGridSectionProps = {
  heading?: React.ReactElement | string;
  headingButton?: React.ReactElement;
  children: ReactNode;
  backgroundColor?: Colors | 'transparent';
  textColor?: Colors;
  columns?: number;
  className?: string;
};

export default function CardGridSection({
  heading,
  headingButton,
  children,
  backgroundColor = '--white',
  textColor = '--stile-grey-8',
  columns = 4,
  className,
}: CardGridSectionProps) {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const headingContent =
    heading || (headingButton && !isMobile) ? (
      <div className="title">
        {heading}
        {!isMobile && headingButton}
      </div>
    ) : undefined;

  return (
    <StyledSection
      backgroundColor={backgroundColor}
      headingContent={headingContent}
      textColor={textColor}
      className={className}
      $columns={columns}
    >
      <div className="card-grid">{children}</div>
      {isMobile && headingButton && <div className="mobile-button-wrapper">{headingButton}</div>}
    </StyledSection>
  );
}
